import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Phone } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import { useLang } from '../context/LanguageContext';
import { teamSeed } from '../data/properties';

// Shared style tokens
const SECTION_PADDING = 'px-6 md:px-12 lg:px-16 py-24';
const CONTAINER = 'max-w-[1200px] mx-auto';
const H2 = 'text-3xl md:text-4xl font-light tracking-tight leading-tight';
const BODY = 'text-[15px] md:text-base leading-relaxed text-neutral-700 max-w-xl';

export default function WhoWeAre() {
  const { t } = useLang();

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-2">
          <div className="px-6 md:px-12 lg:px-16 py-16 lg:py-28 flex flex-col justify-center">
            <SectionLabel>{t.sections.who}</SectionLabel>
            <h1 className="mt-8 text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              {t.nav.who}
            </h1>
            <p className={`mt-8 ${BODY}`}>{t.sections.who_text}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/new-projects"
                className="group inline-flex items-center gap-2 bg-black text-white px-6 py-3 text-sm tracking-[0.15em] hover:bg-[#e11f3a] transition-colors"
              >
                {t.hero.cta_projects}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border border-black px-6 py-3 text-sm tracking-[0.15em] hover:bg-black hover:text-white transition-colors"
              >
                {t.hero.cta_contact}
              </Link>
            </div>
          </div>
          <div className="relative min-h-[420px] lg:min-h-[640px] bg-neutral-100">
            <img
              src="/assets/whoweare/exterior.webp"
              alt="Imodeff building"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section className={SECTION_PADDING}>
        <div className={`${CONTAINER} grid lg:grid-cols-2 gap-16 items-center`}>
          <div className="relative aspect-[4/3] overflow-hidden">
            <img
              src="/assets/whoweare/interior1.webp"
              alt="Experience"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <SectionLabel>{t.sections.experience}</SectionLabel>
            <h2 className={`mt-6 ${H2}`}>30 years of expertise</h2>
            <p className={`mt-6 ${BODY}`}>{t.sections.experience_text}</p>
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className={`bg-neutral-50 ${SECTION_PADDING}`}>
        <div className={`${CONTAINER} grid lg:grid-cols-2 gap-16 items-center`}>
          <div>
            <SectionLabel>{t.sections.vision}</SectionLabel>
            <h2 className={`mt-6 ${H2}`}>{t.sections.vision}</h2>
            <p className={`mt-6 ${BODY}`}>{t.sections.vision_text}</p>
          </div>
          <div className="relative aspect-[4/3] overflow-hidden">
            <img
              src="/assets/whoweare/interior2.webp"
              alt="Vision"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="px-6 md:px-12 lg:px-16 py-24">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-3 gap-4">
          {[
            { src: '/assets/whoweare/inkomhal.png', alt: 'Entrance hall' },
            { src: '/assets/whoweare/bathroom.webp', alt: 'Master bathroom' },
            { src: '/assets/whoweare/living.webp', alt: 'Living room' }
          ].map((img) => (
            <div key={img.src} className="relative aspect-[4/5] overflow-hidden group">
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
            </div>
          ))}
        </div>
      </section>

      {/* HUMAN TEAM */}
      <section className={`bg-neutral-50 ${SECTION_PADDING}`}>
        <div className={`${CONTAINER} grid lg:grid-cols-2 gap-16 items-center`}>
          <div>
            <SectionLabel>{t.sections.human_team}</SectionLabel>
            <h2 className={`mt-6 ${H2}`}>{t.sections.human_team}</h2>
            <p className={`mt-6 ${BODY}`}>{t.sections.human_team_text}</p>
          </div>
          <div className="relative aspect-[4/3] overflow-hidden">
            <img
              src="/assets/whoweare/living.webp"
              alt="Human team"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* OUR TEAM */}
      <section className={SECTION_PADDING}>
        <div className={CONTAINER}>
          <SectionLabel>{t.sections.team}</SectionLabel>
          <h2 className={`mt-6 ${H2}`}>{t.sections.team}</h2>
          <div className="mt-12 grid md:grid-cols-2 gap-10 max-w-3xl mx-auto">
            {teamSeed.map((m) => (
              <div key={m.name} className="text-center">
                <div className="relative w-40 h-40 md:w-48 md:h-48 mx-auto rounded-full overflow-hidden bg-neutral-200">
                  <img src={m.image} alt={m.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="mt-6 text-lg font-semibold">{m.name}</h3>
                <p className="text-[#e11f3a] text-sm mt-1">{m.role}</p>
                {(m.phone || m.email) && (
                  <div className="mt-4 flex flex-col items-center gap-1 text-sm text-neutral-600">
                    {m.phone && (
                      <a
                        href={`tel:${m.phone.replace(/\s/g, '')}`}
                        className="flex items-center gap-2 hover:text-black"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        {m.phone}
                      </a>
                    )}
                    {m.email && (
                      <a
                        href={`mailto:${m.email}`}
                        className="flex items-center gap-2 hover:text-black"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        {m.email}
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className={`bg-black text-white ${SECTION_PADDING}`}>
        <div className="max-w-[1000px] mx-auto text-center">
          <div className="flex justify-center">
            <span className="inline-block w-[3px] h-4 bg-[#e11f3a]" />
          </div>
          <p className="mt-4 text-[13px] tracking-[0.3em] text-neutral-400">
            {t.sections.mission}
          </p>
          <h2 className="mt-8 text-2xl md:text-3xl lg:text-4xl font-light leading-relaxed">
            {t.sections.mission_text}
          </h2>
          <Link
            to="/contact"
            className="mt-10 inline-flex items-center gap-2 border border-white/30 px-6 py-3 text-sm tracking-[0.15em] hover:bg-white hover:text-black transition-colors"
          >
            {t.hero.cta_contact}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
